import { create } from 'zustand';

import type { PropertyValue } from 'state/data/dataState';
import { persist } from 'zustand/middleware';

type ProjectNodePropertyUpdate = {
  projectId: string;
  nodeId: string;
  propertyId: string;
  /**
   * The event ID associated with the property update. This is used to track updates returned via SignalR.
   * If BE failed to process the POST request, eventId will be undefined.
   */
  eventId?: string;
  currentValue: PropertyValue;
  newValue: PropertyValue;
  /** Indicates whether the update has been rolled back */
  hasRolledBack?: boolean;
};

type ProjectNodePropertyUpdatesStore = {
  updates: ProjectNodePropertyUpdate[];
  /**
   * Adds a new update to the store. Overwrite if an update on the same property already exists.
   */
  addUpdates: (update: ProjectNodePropertyUpdate) => void;
  /**
   * Removes an update from the store by its eventId.
   */
  removeUpdate: (update: Omit<ProjectNodePropertyUpdate, 'currentValue' | 'newValue'>) => void;
};

/**
 * Persists user's attempts to update a property value. Remove the update if the attempt was successful.
 * Updating a property value is a long-running task.
 * 1. POST request to the BE to start the updating process. (UI optimistically updates the UI as success)
 * 2. BE sends a SignalR event to  FE that the update was successful.
 *
 * Any of the attempts is persisted in this store. If an attempt passes step 1, it will get an `eventId`.
 * Once an attempt passes the above 2 steps, the update item is removed from the store.
 *
 * The purpose of this store is to keep track of the updates that are in progress or failed. Then we can use this store to:
 * - roll back to the previous value if the update failed
 * - allow user to retry failed updates
 */
export const useProjectNodePropertyUpdatesStore = create<ProjectNodePropertyUpdatesStore>()(
  persist(
    (set) => ({
      updates: [],
      addUpdates: (update) =>
        set(({ updates }) => {
          const index = updates.findIndex((existingUpdate) => isSameUpdate(existingUpdate, update));

          // Overwrite if the update already exists
          if (index !== -1) {
            const nextUpdates = [...updates];
            nextUpdates[index] = update;
            return { updates: nextUpdates };
          }

          return { updates: [...updates, update] };
        }),
      removeUpdate: (update) =>
        set(({ updates }) => ({
          updates: updates.filter((existingUpdate) => !isSameUpdate(existingUpdate, update))
        }))
    }),
    { name: 'project-node-property-updates-store' }
  )
);

function isSameUpdate<T extends Pick<ProjectNodePropertyUpdate, 'projectId' | 'nodeId' | 'propertyId' | 'eventId'>>(
  existingUpdate: T,
  update: T
): boolean {
  if (existingUpdate.eventId && update.eventId) {
    return existingUpdate.eventId === update.eventId;
  }

  return (
    existingUpdate.projectId === update.projectId &&
    existingUpdate.nodeId === update.nodeId &&
    existingUpdate.propertyId === update.propertyId
  );
}
